/**
 * Left-edge drag resize for the docked artifact panel. Pointer-capture+rAF variant (the legacy
 * `hooks/resize-handler.js` is a mouse/document-listener design — this keeps tracking outside the element and
 * commits imperatively). Live drag writes the panel element's width directly (no per-frame React render nor
 * per-pixel Emotion class injection); React state is committed only on pointer-up and on viewport re-clamp. Per
 * the repo's render rules it never reads layout during render: the drag origin lives in refs.
 */

import { useCallback, useEffect, useRef } from "react"
import type { PointerEvent as ReactPointerEvent, RefObject } from "react"

// ~40rem default (assuming the 16px root); bounds are 10vw..75vw, resolved against the live viewport.
export const MIN_PANEL_WIDTH_FRACTION = 0.1
export const DEFAULT_PANEL_WIDTH_PX = 640
export const MAX_PANEL_WIDTH_FRACTION = 0.75

/** Clamp a candidate width to [10vw, 75vw]. */
export function clampPanelWidth(width: number, viewportWidth: number): number {
  const min = Math.round(viewportWidth * MIN_PANEL_WIDTH_FRACTION)
  const max = Math.round(viewportWidth * MAX_PANEL_WIDTH_FRACTION)
  return Math.min(Math.max(width, min), max)
}

export interface ResizeHandleProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
}

export interface UsePanelResizeResult {
  panelRef: RefObject<HTMLDivElement | null>
  resizeHandleProps: ResizeHandleProps
}

export function usePanelResize(width: number, onWidthChange: (width: number) => void): UsePanelResizeResult {
  const panelRef = useRef<HTMLDivElement>(null)
  const dragOriginRef = useRef<{ startX: number, startWidth: number } | null>(null)
  const liveWidthRef = useRef(width)
  const rafRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    liveWidthRef.current = width
  }, [width])
  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current) }, [])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragOriginRef.current = { startX: event.clientX, startWidth: liveWidthRef.current }
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const origin = dragOriginRef.current
    if (origin == null) return
    // Panel is docked on the RIGHT — dragging the handle left (smaller clientX) widens it.
    const next = clampPanelWidth(origin.startWidth + (origin.startX - event.clientX), window.innerWidth)
    liveWidthRef.current = next
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    // Imperative write during the drag: no React render, no per-pixel style-class injection.
    rafRef.current = requestAnimationFrame(() => {
      if (panelRef.current != null) panelRef.current.style.width = `${next}px`
    })
  }, [])

  const endDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (dragOriginRef.current == null) return
    dragOriginRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    // Cancel any frame the last onPointerMove queued so it can't fire after commit and imperatively overwrite the
    // width the controlled render is now the source of truth for.
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = undefined
    }
    // Commit the final width to React so the controlled style stays the source of truth for later renders.
    onWidthChange(liveWidthRef.current)
  }, [onWidthChange])

  return {
    panelRef,
    resizeHandleProps: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag },
  }
}
